import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ChallengeResolveActions from '../ChallengeWire/ChallengeResolveActions';
import ChallengeCard, { challengeCardBtn } from '../ChallengeWire/ChallengeCard';
import HonorCodeModal from '../ChallengeWire/HonorCodeModal';
import { honorCodeFirstName } from '../../constants/honorCode';
import { celebrateChallengeComplete } from '../../utils/celebrationConfetti';
import { challengeHasActions } from '../../hooks/useChallengeActions';
import { getChallengePerspective } from '../../utils/challengePerspective';
import {
	aiTrainerHeadingClassName,
	aiTrainerMutedTextClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

function honorModalProps(challenge) {
	if (!challenge) return { title: 'Accept challenge' };
	if (challenge.kind === 'household' && challenge.challenged_profile?.name) {
		return {
			acceptingAsName: honorCodeFirstName(challenge.challenged_profile.name),
			challengeTitle: challenge.title
		};
	}
	return { title: `Accept: ${challenge.title}` };
}

export default function AiTrainerChallengesRailPanel({
	challenges = [],
	loading = false,
	error = '',
	onRunAction,
	onDayCheck,
	isDayCheckPending,
	actionLoading = false
}) {
	const [honorChallenge, setHonorChallenge] = useState(null);

	const needsYou = useMemo(() => {
		return challenges.filter((challenge) => getChallengePerspective(challenge)?.urgent);
	}, [challenges]);

	function renderChallengeActions(challenge) {
		if (!challengeHasActions(challenge)) return null;

		return (
			<>
				{challenge.can_accept && (
					<>
						<button
							type="button"
							disabled={actionLoading}
							onClick={() => setHonorChallenge(challenge)}
							className={challengeCardBtn.primary}
						>
							Accept
						</button>
						<button
							type="button"
							disabled={actionLoading}
							onClick={() => onRunAction(challenge, 'decline').catch(() => {})}
							className={challengeCardBtn.secondary}
						>
							Decline
						</button>
					</>
				)}
				{challenge.can_fail && (
					<ChallengeResolveActions
						challenge={challenge}
						disabled={actionLoading}
						onComplete={() =>
							onRunAction(challenge, 'complete')
								.then(() => celebrateChallengeComplete())
								.catch(() => {})
						}
						onFail={() => onRunAction(challenge, 'fail').catch(() => {})}
					/>
				)}
				{challenge.can_cancel && (
					<span className={challenge.can_accept || challenge.can_fail ? 'ml-auto' : ''}>
						<button
							type="button"
							disabled={actionLoading}
							onClick={() => onRunAction(challenge, 'cancel').catch(() => {})}
							className={challengeCardBtn.ghost}
						>
							Cancel
						</button>
					</span>
				)}
			</>
		);
	}

	return (
		<div className="px-4 py-4">
			<p className={`mb-3 text-sm font-semibold ${aiTrainerHeadingClassName}`}>Needs your attention</p>

			{error && (
				<p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900 dark:bg-opacity-25 dark:text-red-300">
					{error}
				</p>
			)}

			{loading ? (
				<div className="flex items-center gap-3 py-8 text-sm text-gray-500 dark:text-dark-text-secondary">
					<span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400" />
					Loading challenges…
				</div>
			) : needsYou.length === 0 ? (
				<div className="py-6 text-center">
					<i
						className="fa-solid fa-circle-check text-2xl text-gray-300 dark:text-dark-text-secondary"
						aria-hidden="true"
					/>
					<p className={`mt-3 text-sm font-medium ${aiTrainerHeadingClassName}`}>All caught up</p>
					<p className={`mt-1 text-sm ${aiTrainerMutedTextClassName}`}>
						Nothing needs your attention right now. New challenges from your trainer will show up here.
					</p>
					<Link
						to="/challenge-wire"
						className={`mt-4 inline-flex ${aiTrainerSecondaryButtonClassName} text-xs px-3 py-1.5`}
					>
						Open Challenge Wire
					</Link>
				</div>
			) : (
				<ul className="space-y-4">
					{needsYou.map((challenge) => (
						<li key={`${challenge.kind}-${challenge.id}`} className="h-full min-h-0">
							<ChallengeCard
								challenge={challenge}
								onDayCheck={onDayCheck}
								isDayCheckPending={isDayCheckPending}
							>
								{renderChallengeActions(challenge)}
							</ChallengeCard>
						</li>
					))}
				</ul>
			)}

			{needsYou.length > 0 && (
				<div className="mt-4 border-t border-gray-200 dark:border-dark-border pt-4">
					<Link
						to="/challenge-wire"
						className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
					>
						View all on Challenge Wire →
					</Link>
				</div>
			)}

			<HonorCodeModal
				isOpen={Boolean(honorChallenge)}
				onClose={() => setHonorChallenge(null)}
				onConfirm={() =>
					onRunAction(honorChallenge, 'accept')
						.then(() => setHonorChallenge(null))
						.catch(() => {})
				}
				{...honorModalProps(honorChallenge)}
			/>
		</div>
	);
}
